import { getHFieldWithAppliedHeightScale } from "../../utils/terrainFloor";
import { logger } from "../../utils/logger";
import type { HField, Material, Mesh, Texture } from "../../types/scene";
import { normalizeMeshFileForMjcf } from "./xmlGeneratorConfig";
import { formatMjcfNumber } from "./xmlGenerationHelpers";

type ExportMaterial = Material & {
  metallic?: number | string | null;
  metalness?: number | string | null;
  roughness?: number | string | null;
};

type ExportTexture = Texture & {
  attrs?: Record<string, string>;
};

type ExportMesh = Mesh;
type ExportHField = HField;
const DEFAULT_MAT_COLOR = [0.75, 0.75, 0.75, 1] as const;
const PROCEDURAL_TEXTURE_ATTR_ORDER = [
  "builtin",
  "rgb1",
  "rgb2",
  "mark",
  "markrgb",
  "width",
  "height",
  "nchannel",
  "random",
];

const clamp01 = (value: unknown): number | null => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  return Math.max(0, Math.min(1, numeric));
};

const compareByName = <T extends { name?: string; xmlName?: string }>(a: T, b: T): number =>
  String(a.xmlName || a.name || "").localeCompare(String(b.xmlName || b.name || ""));

const orderedAttrs = (attrs: Record<string, string>, knownOrder: string[]): [string, string][] => {
  const entries = Object.entries(attrs || {}).filter(
    ([key, value]) => key && value != null && key !== "name" && key !== "type" && key !== "file"
  );
  const byKey = new Map(entries);
  const ordered: [string, string][] = [];
  knownOrder.forEach((key) => {
    if (!byKey.has(key)) return;
    ordered.push([key, byKey.get(key)!]);
    byKey.delete(key);
  });
  return [
    ...ordered,
    ...Array.from(byKey.entries()).sort(([keyA], [keyB]) => keyA.localeCompare(keyB)),
  ];
};

export const emitAssetXml = ({
  materials,
  textures,
  meshDefinitions,
  hfieldDefinitions,
  fileMap,
  usedMeshUuids,
  usedHFieldNames,
  materialUuidToName,
  textureUuidToXmlName,
  meshUuidToXmlName,
  hfieldNameToXmlName,
  shouldWarnAboutExportOnlyMaterialLoss,
  resolveMeshPathByUuid,
  resolveHFieldPathByName,
  sanitizeMjcfName,
  xmlEscapeAttr,
  arrayToString,
}: {
  materials: Record<string, ExportMaterial | null | undefined> | undefined;
  textures: Record<string, ExportTexture | undefined> | undefined;
  meshDefinitions: Record<string, ExportMesh | undefined>;
  hfieldDefinitions: Record<string, ExportHField | undefined>;
  fileMap: Record<string, unknown>;
  usedMeshUuids: string[];
  usedHFieldNames: string[];
  materialUuidToName: Record<string, string>;
  textureUuidToXmlName: Record<string, string>;
  meshUuidToXmlName: Record<string, string>;
  hfieldNameToXmlName: Record<string, string>;
  shouldWarnAboutExportOnlyMaterialLoss: boolean;
  resolveMeshPathByUuid: (meshUuid: string) => string | null;
  resolveHFieldPathByName: (name: string) => string | null;
  sanitizeMjcfName: (value: string) => string;
  xmlEscapeAttr: (value: string) => string;
  arrayToString: (values: Array<number | string>) => string;
}): string => {
  const materialEntries = Object.entries(materials ?? {}).sort(
    ([idA, materialA], [idB, materialB]) =>
      String(materialUuidToName[String(idA)] || materialA?.name || idA).localeCompare(
        String(materialUuidToName[String(idB)] || materialB?.name || idB)
      )
  );
  const textureRecords = textures ?? {};
  const meshEntries = usedMeshUuids
    .map((uuid) => ({
      uuid,
      name: meshUuidToXmlName[uuid] || sanitizeMjcfName(`mesh_${uuid}`),
      file: resolveMeshPathByUuid(uuid),
    }))
    .filter((entry): entry is { uuid: string; name: string; file: string } => Boolean(entry.file))
    .sort(compareByName);

  const referencedTextureIds = new Set<string>();
  materialEntries.forEach(([, material]) => {
    if (!material) return;
    const textureId = material.colorMap;
    if (typeof textureId === "string" && textureId.trim()) {
      referencedTextureIds.add(textureId.trim());
    }
  });

  const resolveTextureFilePath = (textureId: string): string | null => {
    const record = textureRecords[textureId] || null;
    const candidate = record?.url || record?.name || textureId;
    if (typeof candidate === "string" && candidate && !candidate.startsWith("blob:")) {
      const normalizedCandidate = candidate.replace(/\\/g, "/");
      if (!normalizedCandidate.includes("/") && fileMap[`assets/${normalizedCandidate}`]) {
        return `assets/${normalizedCandidate}`;
      }
      if (fileMap[normalizedCandidate]) return normalizedCandidate;
    }

    const targetBase = String(candidate)
      .replace(/^.*[\\/]/, "")
      .toLowerCase();
    const match = Object.keys(fileMap || {}).find(
      (key) =>
        String(key)
          .replace(/^.*[\\/]/, "")
          .toLowerCase() === targetBase
    );
    return match || null;
  };

  const usedTextureDefs = Array.from(referencedTextureIds)
    .map((id) => ({
      id,
      name: textureUuidToXmlName[id] || sanitizeMjcfName(`texture_${id}`),
      file: resolveTextureFilePath(id),
      kind: String((textureRecords[id] || {})?.kind || "2d").toLowerCase(),
      attrs: (textureRecords[id] || {})?.attrs || {},
    }))
    .filter((texture) => {
      if (texture.file) return true;
      const proceduralAttrs = Object.keys(texture.attrs || {}).filter(
        (key) => key !== "name" && key !== "file"
      );
      return proceduralAttrs.length > 0;
    })
    .sort(compareByName);
  const usedTextureIdSet = new Set<string>(usedTextureDefs.map((texture) => texture.id));
  const hfieldEntries = usedHFieldNames
    .map((name) => ({
      name,
      xmlName: hfieldNameToXmlName[name] || sanitizeMjcfName(`hfield_${name}`),
      def: hfieldDefinitions[name],
      file: resolveHFieldPathByName(name),
    }))
    .filter(
      (entry): entry is { name: string; xmlName: string; def: ExportHField; file: string | null } =>
        !!entry.def
    )
    .sort(compareByName);

  if (
    materialEntries.length === 0 &&
    meshEntries.length === 0 &&
    usedTextureDefs.length === 0 &&
    hfieldEntries.length === 0
  ) {
    return "";
  }

  let assetXml = "  <asset>\n";
  const emittedTextureNames = new Set<string>();
  usedTextureDefs.forEach(({ name, file, kind, attrs }) => {
    if (!name || emittedTextureNames.has(name)) return;
    emittedTextureNames.add(name);
    const textureType =
      String(kind || attrs?.type || "2d")
        .trim()
        .toLowerCase() || "2d";
    let textureLine = `    <texture name="${name}" type="${xmlEscapeAttr(textureType)}"`;
    if (file) {
      const normalizedPath = xmlEscapeAttr(String(file).replace(/\\/g, "/"));
      textureLine += ` file="${normalizedPath}"`;
    }
    orderedAttrs(attrs || {}, PROCEDURAL_TEXTURE_ATTR_ORDER).forEach(([key, value]) => {
      textureLine += ` ${key}="${xmlEscapeAttr(String(value))}"`;
    });
    textureLine += "/>\n";
    assetXml += textureLine;
  });

  const emittedMaterialNames = new Set<string>();
  materialEntries.forEach(([id, material]) => {
    if (!material) return;
    const matName = materialUuidToName[String(id)] || sanitizeMjcfName(material.name || id);
    if (emittedMaterialNames.has(matName)) return;
    emittedMaterialNames.add(matName);
    const color = Array.isArray(material.color)
      ? [...material.color]
      : material.name === "default_mat"
        ? [...DEFAULT_MAT_COLOR]
        : null;
    const colorMap = material.colorMap;
    const metalness = clamp01(material.metalness ?? material.metallic);
    const roughness = clamp01(material.roughness);
    const emission = clamp01(material.emissiveIntensity);
    const alphaMode = material.alphaMode || (material.transparent ? "blend" : "opaque");
    const usesTextureAlpha =
      typeof colorMap === "string" && colorMap.trim() && alphaMode !== "opaque";
    const exportedColor = color || (usesTextureAlpha ? [1, 1, 1, 1] : null);
    if (usesTextureAlpha && exportedColor) {
      exportedColor[3] = Number(exportedColor[3]) < 1 ? Number(exportedColor[3]) : 0.999;
    }

    let line = `    <material name="${matName}"`;
    if (exportedColor) {
      line += ` rgba="${arrayToString(exportedColor.map((value) => clamp01(value) ?? 0))}"`;
    }
    if (typeof colorMap === "string" && colorMap.trim() && usedTextureIdSet.has(colorMap.trim())) {
      line += ` texture="${textureUuidToXmlName[colorMap.trim()] || sanitizeMjcfName(`texture_${colorMap.trim()}`)}"`;
      const repeat = material.colorMapRepeat;
      if (Array.isArray(repeat) && repeat.length >= 2) {
        line += ` texrepeat="${arrayToString([Number(repeat[0]) || 1, Number(repeat[1]) || 1])}"`;
      }
      const offset = material.colorMapOffset;
      const center = material.colorMapCenter;
      const rotation = Number(material.colorMapRotation ?? 0);
      const hasOffset =
        Array.isArray(offset) &&
        (Math.abs(Number(offset[0]) || 0) > 1e-6 || Math.abs(Number(offset[1]) || 0) > 1e-6);
      const hasCenter =
        Array.isArray(center) &&
        (Math.abs((Number(center[0]) || 0) - 0.5) > 1e-6 ||
          Math.abs((Number(center[1]) || 0) - 0.5) > 1e-6);
      if (hasOffset || hasCenter || Math.abs(Number.isFinite(rotation) ? rotation : 0) > 1e-6) {
        logger.warn(
          `[MJCF export] Material "${matName}" uses color map UV offset/rotation/center, which MJCF material assets cannot represent. Exporting texture repeat only.`
        );
      }
    }
    if (metalness != null) {
      line += ` metallic="${formatMjcfNumber(metalness)}"`;
    }
    if (roughness != null) {
      line += ` roughness="${formatMjcfNumber(roughness)}"`;
    }
    if (emission != null && emission > 0) {
      line += ` emission="${formatMjcfNumber(emission)}"`;
    }

    const materialRecord = material as ExportMaterial & Record<string, unknown>;
    const unsupportedPhysicalMaterialAttrs = [
      "clearcoat",
      "clearcoatRoughness",
      "transmission",
      "thickness",
      "ior",
      "specularIntensity",
      "specularColor",
      "anisotropy",
      "iridescence",
    ].filter((key) => materialRecord[key] !== undefined && materialRecord[key] !== null);
    if (shouldWarnAboutExportOnlyMaterialLoss && unsupportedPhysicalMaterialAttrs.length > 0) {
      logger.warn(
        `[MJCF export] Material "${matName}" has unsupported physical material properties (${unsupportedPhysicalMaterialAttrs.join(", ")}). MuJoCo material XML cannot represent these; exporting supported material attributes only.`
      );
    }

    line += "/>\n";
    assetXml += line;
  });

  meshEntries.forEach(({ uuid, name, file }) => {
    const cleaned = normalizeMeshFileForMjcf(file);
    const meshMeta = meshDefinitions[uuid] || null;
    const scale = meshMeta?.scale;
    const inertiaComputation = meshMeta?.inertiaComputation;
    let meshLine = `    <mesh name="${name}" file="${xmlEscapeAttr(cleaned)}"`;
    if (Array.isArray(scale) && scale.length === 3) {
      meshLine += ` scale="${arrayToString(scale)}"`;
    }
    if (
      inertiaComputation === "convex" ||
      inertiaComputation === "exact" ||
      inertiaComputation === "legacy" ||
      inertiaComputation === "shell"
    ) {
      meshLine += ` inertia="${inertiaComputation}"`;
    }
    meshLine += "/>\n";
    assetXml += meshLine;
  });

  hfieldEntries.forEach(({ xmlName, def, file }) => {
    const defForExport = def ? getHFieldWithAppliedHeightScale(def) : null;
    const size =
      Array.isArray(defForExport?.size) && defForExport.size.length >= 4
        ? defForExport.size.slice(0, 4)
        : [1, 1, 0.1, 0.01];
    let line = `    <hfield name="${xmlName}" size="${arrayToString(size)}"`;
    if (def?.contentType) {
      line += ` content_type="${def.contentType}"`;
    }
    if (file) {
      line += ` file="${xmlEscapeAttr(String(file).replace(/\\/g, "/"))}"`;
    }
    if (Number.isFinite(Number(def?.nrow))) line += ` nrow="${formatMjcfNumber(Number(def.nrow))}"`;
    if (Number.isFinite(Number(def?.ncol))) line += ` ncol="${formatMjcfNumber(Number(def.ncol))}"`;
    if (!file && Array.isArray(def?.data) && def.data.length > 0) {
      line += ` elevation="${arrayToString(def.data)}"`;
    }
    line += "/>\n";
    assetXml += line;
  });

  assetXml += "  </asset>\n\n";
  return assetXml;
};
